import { useEffect, useState } from "react";
import { Checkbox } from '@/shared/components/Checkbox'
import { LLAVE_MOTIVO_CIERRE } from "@/shared/lib/http";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import ProviderLoginImg from '@/assets/provider-login.webp';
import bigLogo from '@/assets/big-logo.svg';
import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService';
import { PROVIDER_INPUT_CLASS } from '@/features/proveedores/components/providerFormTokens';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface LoginProviderProps {
  onLogin: (
    email: string,
    password: string
  ) => Promise<{ firstName: string; lastName: string } | void>;
  isLoading: boolean;
  error: string | null;
}

const LoginProviders = ({ onLogin, isLoading: _externalLoading, error: externalError }: LoginProviderProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (localError) setLocalError(null);
    if (successMessage) setSuccessMessage(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      const response = await supplierAuthService.login({
        email: formData.email,
        password: formData.password,
      });

      supplierAuthService.saveToken(response.access_token);
      supplierAuthService.saveSupplierData(response.supplier);
      await onLogin(formData.email, formData.password);

      setSuccessMessage('Inicio de sesión exitoso. Redirigiendo...');
      
      setTimeout(() => {
        navigate('/proveedores/dashboard');
      }, 1000);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Si llegamos aquí por una sesión caída, decirlo. Antes el proveedor
   * simplemente aparecía en el login a media tarea, sin explicación.
   */
  const [motivoCierre, setMotivoCierre] = useState<string | null>(null);
  useEffect(() => {
    try {
      const motivo = sessionStorage.getItem(LLAVE_MOTIVO_CIERRE);
      if (motivo) {
        setMotivoCierre(motivo);
        sessionStorage.removeItem(LLAVE_MOTIVO_CIERRE);
      }
    } catch {
      // Sin acceso al almacenamiento no hay aviso que mostrar.
    }
  }, []);

  const displayError = externalError || localError || motivoCierre;

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left Side - Form Section */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          {/* Logo - Centrado y Grande */}
          <div className="mb-8 flex flex-col items-center">
            <img src={bigLogo} alt="SLYRSA Logo" className="h-20 w-auto mb-2" />
          </div>

          {/* Welcome Text */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Accede a tu cuenta
            </h1>
            <p className="text-sm text-gray-600">
              Ingresa tu correo y contraseña para iniciar sesión
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {displayError && !successMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-800">{displayError}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Correo electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={PROVIDER_INPUT_CLASS}
                placeholder="correo@example.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={PROVIDER_INPUT_CLASS}
                  placeholder="••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <Checkbox
                id="remember-me"
                name="remember-me"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-700"
              >
                Recordar contraseña
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={estiloDeBoton('primario', 'w-full')}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Iniciando sesión...
                </div>
              ) : (
                "Iniciar sesión"
              )}
            </button>

            {/* Create Account Button */}
            <button
              type="button"
              onClick={() => navigate('/proveedores/registro')}
              className={estiloDeBoton('secundario', 'w-full')}
            >
              Crear cuenta
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <a
              href="#"
              className="text-sm text-[#3B5998] hover:text-[#344e86] transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </div>
      </div>

      {/* Right Side - Features Section with Gradient in Rounded Box */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        {/* Rounded White Container */}
        <div className="rounded-[32px] w-full h-[calc(100vh-3rem)] flex flex-col items-center relative overflow-hidden shadow-xl">
          {/* Features List - Más abajo */}
          <div className="relative z-30 w-full max-w-lg space-y-4">
            <div className="absolute top-5   sm:right-[20px] xl:right-[-50px] 2xl:right-[-180px] bg-white w-[154px] h-[35px] rounded-full flex justify-center items-center">
              <p className="font-semibold">Proveedor</p>
            </div>
            {/* Feature 1 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[500px] px-7 py-4 flex items-start space-x-3 absolute top-[130px] z-30 mt-12">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-4 h-4 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Administra, monitorea y da seguimiento a tus proveedores en un
                solo sistema.
              </p>
            </div>
          </div>

          {/* Tablet Image - Alineado al fondo */}
          <div className="relative z-10 w-full h-full">
            <div className="absolute inset-0 rounded-[32px] blur-2xl pointer-events-none bg-gradient-to-t from-[#1FB7EE]/10  to-[#315FB9]/80 "></div>
            <img
              src={ProviderLoginImg}
              alt="Login Proveedor"
              className=" w-full h-auto object-center"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginProviders;
